import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userAPI } from '../../services/api';
import '../../styles/admin/UserList.css';
import { useDesktopViewport } from '../../hooks/useDesktopViewport';

const UserList = () => {
    useDesktopViewport();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [newUser, setNewUser] = useState({
        username: '',
        password: '',
        email: '',
        phone: '',
        role: 'user'
    });
    const [selectedUser, setSelectedUser] = useState(null);
    const [showExposureModal, setShowExposureModal] = useState(false);
    const [exposureLimit, setExposureLimit] = useState('');
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [showDepositModal, setShowDepositModal] = useState(false);
    const [depositAmount, setDepositAmount] = useState('');
    const [showWithdrawModal, setShowWithdrawModal] = useState(false);
    const [withdrawAmount, setWithdrawAmount] = useState('');
    
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            const response = await userAPI.getDownline();
            setUsers(response.data);
        } catch (error) {
            console.error('Error loading users:', error);
        } finally {
            setLoading(false);
        }
    };

    const getRoleBadgeClass = (role) => {
        const classes = {
            admin: 'badge-danger',
            super_master: 'badge-warning',
            master: 'badge-info',
            agent: 'badge-primary',
            user: 'badge-secondary'
        };
        return classes[role] || 'badge-secondary';
    };

    const getRoleIcon = (role) => {
        const icons = {
            admin: '👑',
            super_master: '⭐',
            master: '🔷',
            agent: '🔶',
            user: '👤'
        };
        return icons[role] || '📊';
    };

    const toggleUserStatus = async (userId, currentStatus) => {
        if (window.confirm(`Are you sure you want to ${currentStatus === 'active' ? 'deactivate' : 'activate'} this user?`)) {
            try {
                await userAPI.toggleUserStatus(userId);
                await loadUsers();
                alert(`User ${currentStatus === 'active' ? 'deactivated' : 'activated'} successfully!`);
            } catch (error) {
                alert(error.response?.data?.message || 'Error toggling user status');
            }
        }
    };

    const toggleBettingStatus = async (userId, currentBetStatus) => {
        if (window.confirm(`Are you sure you want to ${currentBetStatus === 'active' ? 'disable' : 'enable'} betting for this user?`)) {
            try {
                await userAPI.toggleBettingStatus(userId);
                await loadUsers();
                alert(`Betting ${currentBetStatus === 'active' ? 'disabled' : 'enabled'} for user!`);
            } catch (error) {
                alert(error.response?.data?.message || 'Error toggling betting status');
            }
        }
    };

    const updateExposureLimit = async (userId) => {
        if (!exposureLimit || parseFloat(exposureLimit) < 0) {
            alert('Please enter a valid exposure limit');
            return;
        }
        try {
            await userAPI.updateExposureLimit(userId, parseFloat(exposureLimit));
            await loadUsers();
            alert('Exposure limit updated successfully!');
            setShowExposureModal(false);
            setExposureLimit('');
            setSelectedUser(null);
        } catch (error) {
            alert(error.response?.data?.message || 'Error updating exposure limit');
        }
    };

    const changePassword = async (userId) => {
        if (!newPassword || newPassword.length < 4) {
            alert('Password must be at least 4 characters');
            return;
        }
        try {
            await userAPI.changeUserPassword(userId, newPassword);
            alert('Password changed successfully!');
            setShowPasswordModal(false);
            setNewPassword('');
            setSelectedUser(null);
        } catch (error) {
            alert(error.response?.data?.message || 'Error changing password');
        }
    };

    const depositBalance = async (userId) => {
        if (!depositAmount || parseFloat(depositAmount) <= 0) {
            alert('Please enter a valid amount');
            return;
        }
        try {
            await userAPI.creditUser(userId, parseFloat(depositAmount));
            await loadUsers();
            alert(`₹${depositAmount} deposited successfully!`);
            setShowDepositModal(false);
            setDepositAmount('');
            setSelectedUser(null);
        } catch (error) {
            alert(error.response?.data?.message || 'Error depositing balance');
        }
    };

    const withdrawBalance = async (userId) => {
        if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
            alert('Please enter a valid amount');
            return;
        }
        try {
            await userAPI.debitUser(userId, parseFloat(withdrawAmount));
            await loadUsers();
            alert(`₹${withdrawAmount} withdrawn successfully!`);
            setShowWithdrawModal(false);
            setWithdrawAmount('');
            setSelectedUser(null);
        } catch (error) {
            alert(error.response?.data?.message || 'Error withdrawing balance');
        }
    };

    const createUser = async (e) => {
        e.preventDefault();
        try {
            await userAPI.createUser(newUser);
            alert('User created successfully!');
            setShowAddModal(false);
            setNewUser({ username: '', password: '', email: '', phone: '', role: 'user' });
            await loadUsers();
        } catch (error) {
            alert(error.response?.data?.message || 'Error creating user');
        }
    };

    const filteredUsers = users.filter(user => {
        if (filterStatus !== 'all' && user.status !== filterStatus) return false;
        if (searchTerm && !user.username.toLowerCase().includes(searchTerm.toLowerCase())) return false;
        return true;
    });

    if (loading) {
        return <div className="loading">Loading users...</div>;
    }

    return (
        <div className="user-list-container">
            <div className="header">
                <h1>Client Management</h1>
                <button onClick={() => setShowAddModal(true)} className="btn-primary">
                    ➕ Add Account
                </button>
            </div>

            {/* Filters */}
            <div className="filters-bar">
                <div className="filter-tabs">
                    <button 
                        className={`filter-tab ${filterStatus === 'all' ? 'active' : ''}`}
                        onClick={() => setFilterStatus('all')}
                    >
                        All ({users.length})
                    </button>
                    <button 
                        className={`filter-tab ${filterStatus === 'active' ? 'active' : ''}`}
                        onClick={() => setFilterStatus('active')}
                    >
                        Active ({users.filter(u => u.status === 'active').length})
                    </button>
                    <button 
                        className={`filter-tab ${filterStatus === 'inactive' ? 'active' : ''}`}
                        onClick={() => setFilterStatus('inactive')}
                    >
                        Inactive ({users.filter(u => u.status === 'inactive').length})
                    </button>
                </div>
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="Search by username..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Users Table */}
            <div className="table-container">
                <table className="users-table">
                    <thead>
                        <tr>
                            <th>Username</th>
                            <th>Balance</th>
                            <th>P/L</th>
                            <th>Exposure Limit</th>
                            <th>Account Type</th>
                            <th>Status</th>
                            <th>Bet Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.map((user) => (
                            <tr key={user._id}>
                                <td>
                                    <div className="user-cell">
                                        <span className="role-icon">{getRoleIcon(user.role)}</span>
                                        <span>{user.username}</span>
                                    </div>
                                </td>
                                <td className="balance">₹{user.balance?.toLocaleString()}</td>
                                <td className={user.profitLoss >= 0 ? 'profit' : 'loss'}>
                                    {user.profitLoss >= 0 ? '+' : ''}₹{user.profitLoss?.toLocaleString()}
                                </td>
                                <td>₹{user.exposureLimit?.toLocaleString() || 'Unlimited'}</td>
                                <td>
                                    <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                                        {user.role.replace('_', ' ').toUpperCase()}
                                    </span>
                                </td>
                                <td>
                                    <button 
                                        className={`status-toggle ${user.status}`}
                                        onClick={() => toggleUserStatus(user._id, user.status)}
                                    >
                                        {user.status === 'active' ? '🟢 Active' : '🔴 Inactive'}
                                    </button>
                                </td>
                                <td>
                                    <button 
                                        className={`bet-toggle ${user.bettingEnabled ? 'active' : 'inactive'}`}
                                        onClick={() => toggleBettingStatus(user._id, user.bettingEnabled)}
                                    >
                                        {user.bettingEnabled ? '🎲 On' : '⛔ Off'}
                                    </button>
                                </td>
                                <td className="actions-cell">
                                    <button 
                                        onClick={() => {
                                            setSelectedUser(user);
                                            setDepositAmount('');
                                            setShowDepositModal(true);
                                        }}
                                        className="btn-action deposit"
                                        title="Deposit"
                                    >
                                        💰
                                    </button>
                                    <button 
                                        onClick={() => {
                                            setSelectedUser(user);
                                            setWithdrawAmount('');
                                            setShowWithdrawModal(true);
                                        }}
                                        className="btn-action withdraw"
                                        title="Withdraw"
                                    >
                                        💸
                                    </button>
                                    <button 
                                        onClick={() => {
                                            setSelectedUser(user);
                                            setExposureLimit(user.exposureLimit || '');
                                            setShowExposureModal(true);
                                        }}
                                        className="btn-action exposure"
                                        title="Set Exposure Limit"
                                    >
                                        ⚖️
                                    </button>
                                    <button 
                                        onClick={() => {
                                            setSelectedUser(user);
                                            setNewPassword('');
                                            setShowPasswordModal(true);
                                        }}
                                        className="btn-action password"
                                        title="Change Password"
                                    >
                                        🔒
                                    </button>
                                    <button 
                                        onClick={() => navigate(`/admin/downline/${user._id}`)}
                                        className="btn-action view"
                                        title="View Downline"
                                    >
                                        👥
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {filteredUsers.length === 0 && (
                            <tr>
                                <td colSpan="8" className="text-center">No users found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modals - same as before */}
            {showAddModal && (
                <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Create New Account</h2>
                        <form onSubmit={createUser}>
                            <input type="text" placeholder="Username" value={newUser.username} onChange={(e) => setNewUser({...newUser, username: e.target.value})} required />
                            <input type="password" placeholder="Password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} required />
                            <input type="email" placeholder="Email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} required />
                            <input type="tel" placeholder="Phone" value={newUser.phone} onChange={(e) => setNewUser({...newUser, phone: e.target.value})} required />
                            <select value={newUser.role} onChange={(e) => setNewUser({...newUser, role: e.target.value})}>
                                <option value="user">User</option>
                                <option value="agent">Agent</option>
                                <option value="master">Master</option>
                                <option value="super_master">Super Master</option>
                            </select>
                            <div className="modal-actions">
                                <button type="button" onClick={() => setShowAddModal(false)}>Cancel</button>
                                <button type="submit">Create</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Other modals - deposit, withdraw, exposure, password */}
            {/* Add similar modal components here */}
        </div>
    );
};

export default UserList;